import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  AuthPanel,
  isProfileStepRequired,
  parseAuthError,
  resolveAuthSuccessPath,
  unlockRegistrationProfileStep,
  useAuth,
} from '@/features/auth'
import { routes } from '@/shared/config'
import { Button, PasswordInput } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'
import { Footer } from '@/widgets/footer'
import { Header } from '@/widgets/header'
import bgEpic from '@/shared/assets/registration/bg_epic.png'

const PASSWORD_REQUIRED_MESSAGE = 'Введите новый пароль длиной не менее 8 символов.'
const INVALID_LINK_MESSAGE = 'Ссылка недействительна'

const registrationDesktopTokens = designTokens.components.registration.desktop
const registrationMobileTokens = designTokens.components.registration.mobile

const helperTextStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  fontSize: designTokens.typography.body.xsNormal.size,
  lineHeight: designTokens.typography.body.xsNormal.lineHeight,
  fontWeight: designTokens.typography.body.xsNormal.weight,
  margin: 0,
  textAlign: 'center' as const,
}

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { confirmPasswordReset } = useAuth()
  const [password, setPassword] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const token = searchParams.get('token')?.trim() ?? ''
  const hasToken = token.length > 0

  useEffect(() => {
    document.title = 'Сброс пароля | Epic Growth'
  }, [])

  const handleSubmit = async () => {
    if (isSubmitting) return
    if (!hasToken) {
      setErrorMessage(INVALID_LINK_MESSAGE)
      return
    }

    if (password.trim().length < 8) {
      setErrorMessage(PASSWORD_REQUIRED_MESSAGE)
      return
    }

    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      const response = await confirmPasswordReset({ token, password })
      const requiresProfileStep = isProfileStepRequired(response)
      if (requiresProfileStep) {
        unlockRegistrationProfileStep()
      }
      navigate(resolveAuthSuccessPath(response), {
        replace: true,
        state: requiresProfileStep ? { allowProfileStepOnce: true } : null,
      })
    } catch (error) {
      setErrorMessage(parseAuthError(error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section
      className="relative w-full"
      style={{
        minHeight: '100vh',
        width: '100%',
        backgroundImage: `url(${bgEpic})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflowX: 'hidden',
      }}
    >
      <div
        className="relative hidden min-h-screen w-full flex-col items-center lg:flex"
        style={{ maxWidth: registrationDesktopTokens.frame.width }}
      >
        <div className="w-full">
          <Header />
        </div>
        <main className="relative flex w-full flex-1 items-start justify-center">
          <div
            className="relative z-10 w-full"
            style={{
              maxWidth: registrationDesktopTokens.panel.width,
              marginTop: designTokens.spacing.authPanelDesktopTop,
            }}
          >
            <AuthPanel
              variant="desktop"
              title="Сбросьте пароль"
              subtitle="Введите новый пароль для входа в Epic Growth"
              footer={
                <div
                  className="flex w-full flex-col"
                  style={{ gap: designTokens.spacing.authPanelFooterGap }}
                >
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !hasToken}
                  >
                    {isSubmitting ? 'Сохраняем...' : 'Подтвердить'}
                  </Button>
                  {errorMessage ? (
                    <p style={{ ...helperTextStyle, color: designTokens.colors.text.error }}>
                      {errorMessage}
                    </p>
                  ) : null}
                  {!hasToken ? (
                    <div className="flex w-full justify-center">
                      <button
                        type="button"
                        style={{ ...helperTextStyle, color: designTokens.colors.text.primary }}
                        onClick={() => navigate(routes.registration)}
                      >
                        Вернуться ко входу
                      </button>
                    </div>
                  ) : null}
                </div>
              }
            >
              <PasswordInput
                label="Новый пароль"
                placeholder="Введите новый пароль"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value)
                  setErrorMessage(null)
                }}
                helperText={!hasToken ? INVALID_LINK_MESSAGE : undefined}
                helperClassName="text-center"
              />
            </AuthPanel>
          </div>
        </main>
        <div className="w-full" style={{ maxWidth: registrationDesktopTokens.footer.width }}>
          <Footer />
        </div>
      </div>

      <div className="relative flex min-h-screen w-full flex-col items-center lg:hidden">
        <div
          className="relative w-full flex-1"
          style={{
            minHeight: registrationMobileTokens.topAreaMinHeightTall,
            maxWidth: registrationMobileTokens.frame.width,
          }}
        >
          <div
            className="absolute"
            style={{
              left: '50%',
              top: registrationMobileTokens.headerCompact.y,
              width: registrationMobileTokens.headerCompact.width,
              height: registrationMobileTokens.headerCompact.height,
              transform: 'translateX(-50%)',
            }}
          >
            <Header />
          </div>
        </div>
        <div className="mt-auto w-full">
          <AuthPanel
            variant="mobile"
            title="Сбросьте пароль"
            subtitle="Введите новый пароль для входа в Epic Growth"
            footer={
              <div
                className="flex w-full flex-col"
                style={{ gap: designTokens.spacing.authPanelFooterGap }}
              >
                <Button
                  variant="primaryMobile"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !hasToken}
                >
                  {isSubmitting ? 'Сохраняем...' : 'Подтвердить'}
                </Button>
                {errorMessage ? (
                  <p style={{ ...helperTextStyle, color: designTokens.colors.text.error }}>
                    {errorMessage}
                  </p>
                ) : null}
                {!hasToken ? (
                  <div className="flex w-full justify-center">
                    <button
                      type="button"
                      style={{ ...helperTextStyle, color: designTokens.colors.text.primary }}
                      onClick={() => navigate(routes.registration)}
                    >
                      Вернуться ко входу
                    </button>
                  </div>
                ) : null}
              </div>
            }
          >
            <PasswordInput
              label="Новый пароль"
              placeholder="Введите новый пароль"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value)
                setErrorMessage(null)
              }}
              helperText={!hasToken ? INVALID_LINK_MESSAGE : undefined}
              helperClassName="text-center"
            />
          </AuthPanel>
        </div>
      </div>
    </section>
  )
}
